import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginPrompt } from '../components/LoginPrompt';
import StopGameSolo from '../components/stop-game/StopGameSolo';
import StopGameMultiplayer from '../components/stop-game/StopGameMultiplayer';
import StopGameParty from '../components/stop-game/StopGameParty';
import { Sparkles, User, Users, Home, BookOpen, Volume2, CheckCircle2, ShieldCheck, StopCircle } from 'lucide-react';

export type StopGameMode = 'solo' | 'multiplayer' | 'party' | null;

export const StopGamePage: React.FC = () => {
  const { currentUser, loading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const roomParam = searchParams.get('room');
  const modeParam = searchParams.get('mode') as StopGameMode;

  const hasSavedSoloGame = () => {
    try {
      const saved = localStorage.getItem('stop_game_solo_state');
      if (saved) {
        const parsed = JSON.parse(saved);
        return Boolean(parsed && parsed.phase && parsed.phase !== 'setup');
      }
    } catch {
      return false;
    }
    return false;
  };

  const [activeMode, setActiveMode] = useState<StopGameMode>(() => {
    if (roomParam) return 'multiplayer';
    if (modeParam === 'solo' || modeParam === 'multiplayer' || modeParam === 'party') return modeParam;
    if (hasSavedSoloGame()) return 'solo';
    return null;
  });

  useEffect(() => {
    if (roomParam) {
      setActiveMode('multiplayer');
    }
  }, [roomParam]);

  const handleSelectMode = (mode: StopGameMode) => {
    setActiveMode(mode);
    if (mode === 'solo') {
      setSearchParams({ mode: 'solo' });
    } else if (mode === 'party') {
      setSearchParams({ mode: 'party' });
    } else {
      setSearchParams({});
    }
  };

  const handleBackToMenu = () => {
    try {
      localStorage.removeItem('stop_game_solo_state');
    } catch {
      // ignore
    }
    setActiveMode(null);
    setSearchParams({});
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (!currentUser) {
    return (
      <LoginPrompt
        title="Login Required to Play STOP!"
        description="You need to be logged in to play the STOP! Word Challenge, compete with friends, and save your English vocabulary progress. Create an account or sign in to start playing!"
        featureName="STOP! Game"
        icon={<StopCircle className="w-10 h-10 text-purple-600" />}
      />
    );
  }

  if (activeMode === 'solo') {
    return <StopGameSolo onBackToMenu={handleBackToMenu} />;
  }

  if (activeMode === 'multiplayer') {
    return (
      <StopGameMultiplayer
        initialRoomCode={roomParam || undefined}
        onBackToMenu={handleBackToMenu}
      />
    );
  }

  if (activeMode === 'party') {
    return <StopGameParty onBackToMenu={handleBackToMenu} />;
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      {/* Hero Banner */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-100 text-purple-700 font-extrabold text-sm mb-4">
          <Sparkles className="w-4 h-4 text-purple-600" />
          English Vocabulary Game
        </div>
        <h1 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight">
          STOP! Word Challenge
        </h1>
        <p className="text-gray-600 mt-3 text-lg max-w-2xl mx-auto">
          Learn and expand your English vocabulary in the classic STOP game! All words are verified in real time against the English dictionary API with definitions and audio pronunciation.
        </p>

        <div className="flex flex-wrap items-center justify-center gap-4 mt-6 text-xs font-bold text-gray-500">
          <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full border border-gray-200 shadow-sm">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            <span>Dictionary API Checked</span>
          </div>
          <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full border border-gray-200 shadow-sm">
            <Volume2 className="w-4 h-4 text-indigo-600" />
            <span>Audio Pronunciations</span>
          </div>
          <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full border border-gray-200 shadow-sm">
            <BookOpen className="w-4 h-4 text-purple-600" />
            <span>Vocabulary Definitions</span>
          </div>
        </div>
      </div>

      {/* 3 Modes Selection Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        {/* MODE 1: SOLO ONLINE */}
        <div
          onClick={() => handleSelectMode('solo')}
          className="bg-white rounded-3xl p-6 md:p-8 shadow-xl border border-gray-100 hover:border-purple-300 hover:shadow-2xl transition-all duration-300 flex flex-col justify-between cursor-pointer group transform hover:-translate-y-1"
        >
          <div>
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-purple-500 to-indigo-600 text-white flex items-center justify-center mb-6 shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform">
              <User className="w-7 h-7" />
            </div>

            <span className="text-xs font-black uppercase text-purple-600 tracking-wider">
              Single Player
            </span>
            <h2 className="text-2xl font-black text-gray-900 mt-1">Play Online Alone</h2>
            <p className="text-gray-600 text-sm mt-3 leading-relaxed">
              Practice solo against the clock! Great for testing your English speed, memorizing new words, and reviewing dictionary definitions.
            </p>

            <ul className="mt-6 space-y-2 text-xs font-semibold text-gray-500">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                Custom timer &amp; round settings
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                Real-time API word validation
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                Instant vocabulary review &amp; audio
              </li>
            </ul>
          </div>

          <div className="mt-8">
            <button className="w-full py-3.5 bg-gradient-to-r from-purple-600 to-indigo-600 group-hover:from-purple-700 group-hover:to-indigo-700 text-white font-extrabold text-sm rounded-2xl shadow-lg shadow-purple-500/20 transition-all">
              Play Solo 🎯
            </button>
          </div>
        </div>

        {/* MODE 2: MULTIPLAYER ONLINE (INVITE A FRIEND) */}
        <div
          onClick={() => handleSelectMode('multiplayer')}
          className="bg-white rounded-3xl p-6 md:p-8 shadow-xl border-2 border-purple-200 hover:border-purple-400 hover:shadow-2xl transition-all duration-300 flex flex-col justify-between cursor-pointer group transform hover:-translate-y-1 relative overflow-hidden"
        >
          <div className="absolute top-4 right-4 bg-purple-100 text-purple-700 font-black text-[10px] uppercase px-2.5 py-1 rounded-full">
            Most Popular
          </div>

          <div>
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-indigo-600 to-pink-500 text-white flex items-center justify-center mb-6 shadow-lg shadow-indigo-500/25 group-hover:scale-110 transition-transform">
              <Users className="w-7 h-7" />
            </div>

            <span className="text-xs font-black uppercase text-indigo-600 tracking-wider">
              Real-Time Multiplayer
            </span>
            <h2 className="text-2xl font-black text-gray-900 mt-1">Play with a Friend</h2>
            <p className="text-gray-600 text-sm mt-3 leading-relaxed">
              Create a private room and send a link or room code to invite a friend to play together online in real time.
            </p>

            <ul className="mt-6 space-y-2 text-xs font-semibold text-gray-500">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                Synchronized room &amp; letter wheel
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                First to hit STOP buzzes the round
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                Scores 10 for unique / 5 for duplicate words
              </li>
            </ul>
          </div>

          <div className="mt-8">
            <button className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-pink-600 group-hover:from-indigo-700 group-hover:to-pink-700 text-white font-extrabold text-sm rounded-2xl shadow-lg shadow-indigo-500/20 transition-all">
              Invite a Friend 👥
            </button>
          </div>
        </div>

        {/* MODE 3: IN-PERSON / OFFLINE ROOM MODE */}
        <div
          onClick={() => handleSelectMode('party')}
          className="bg-white rounded-3xl p-6 md:p-8 shadow-xl border border-gray-100 hover:border-emerald-300 hover:shadow-2xl transition-all duration-300 flex flex-col justify-between cursor-pointer group transform hover:-translate-y-1"
        >
          <div>
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-emerald-500 to-teal-600 text-white flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/25 group-hover:scale-110 transition-transform">
              <Home className="w-7 h-7" />
            </div>

            <span className="text-xs font-black uppercase text-emerald-600 tracking-wider">
              Tabletop &amp; Classroom
            </span>
            <h2 className="text-2xl font-black text-gray-900 mt-1">Party / In-Person Mode</h2>
            <p className="text-gray-600 text-sm mt-3 leading-relaxed">
              Gather everyone in the same room in real life! Use the website as the central board, letter spinner, master timer, and live Dictionary Referee.
            </p>

            <ul className="mt-6 space-y-2 text-xs font-semibold text-gray-500">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                Big screen display &amp; sound buzzer
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                Live dictionary dispute resolver
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                Automated room scorekeeper
              </li>
            </ul>
          </div>

          <div className="mt-8">
            <button className="w-full py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 group-hover:from-emerald-700 group-hover:to-teal-700 text-white font-extrabold text-sm rounded-2xl shadow-lg shadow-emerald-500/20 transition-all">
              Party / Classroom Board 🏠
            </button>
          </div>
        </div>
      </div>

      {/* Educational Rules & How to Play */}
      <div className="bg-gradient-to-r from-purple-50 via-indigo-50 to-pink-50 rounded-3xl p-6 md:p-8 border border-purple-100">
        <h3 className="text-xl font-black text-gray-900 mb-3 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-purple-600" />
          How the STOP Game Works &amp; Scoring Rules
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-gray-600 font-medium">
          <div className="bg-white/80 p-4 rounded-2xl border border-purple-100">
            <strong className="block text-gray-900 text-sm mb-1 font-bold">1. Spin the Letter</strong>
            Each round selects a random letter from the alphabet (e.g. &ldquo;B&rdquo;). All words you write in every category must start with that letter.
          </div>
          <div className="bg-white/80 p-4 rounded-2xl border border-purple-100">
            <strong className="block text-gray-900 text-sm mb-1 font-bold">2. Race to Say STOP!</strong>
            Fill in words for Animal, Food, Country/City, Job, Object, Verb, etc. Hit STOP as fast as you can to challenge your opponents.
          </div>
          <div className="bg-white/80 p-4 rounded-2xl border border-purple-100">
            <strong className="block text-gray-900 text-sm mb-1 font-bold">3. Dictionary Check</strong>
            <ul className="mt-1 space-y-1">
              <li>• <strong>10 Points:</strong> Valid unique English word</li>
              <li>• <strong>5 Points:</strong> Valid word shared with another player</li>
              <li>• <strong>0 Points:</strong> Non-English or invalid word</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StopGamePage;
